import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { Close, CollapseContent, Delete, ExpandContent } from '@tempo-io/sui-icons';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@tempo-io/sui-sheet';
import { Spinner } from '@tempo-io/sui-spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Item, ItemAncestor } from 'src/lib/api/types/items';
import { TooltipIconButton } from '@/components/ui/tooltipIconButton';
import { useCurrentUserId } from '@/lib/hooks/common/useCurrentUserId';
import { useItemPanelNavigation } from '@/lib/hooks/item-panel/useItemPanelParams';
import { useGetAllWorkIntegrationsById } from '@/lib/queries/work-integrations/useGetAllWorkIntegrations';
import { useGetItemWorkIntegration } from '@/lib/queries/work-integrations/useGetItemWorkIntegration';
import type { TabValueType } from '@/lib/types/itemPanel';
import { BreadcrumbAncestors } from './BreadcrumbAncestors';
import { TabAdapterProvider } from './context/TabAdapterContext';
import { DeleteItemDialog } from './DeleteItemDialog';
import { useItemFetchQueryWithPublished } from './hooks/useItemFetchQueryWithPublished';
import { usePermissionsWithPublished } from './hooks/usePermissionsWithPublished';
import { ItemNameField } from './ItemNameField';
import { ItemDetails } from './tabs/ItemDetails';
import type { TabConfig, ViewPanelConfig } from './types';

interface ItemPanelProps {
  itemId?: number;
  item?: Item;
  viewPanel: ViewPanelConfig;
  tabs: TabConfig[];
  expanded: boolean;
  panelTab: TabValueType;
  onJumpToItem?: (itemId: number) => void;
  onTabChange?: (tab: TabValueType) => void;
  open?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  customFieldDefinitions?: CustomFieldDTO[];
  enableAncestors?: boolean;
  staticAncestors?: ItemAncestor[];
  disableAncestorNavigation?: boolean;
}

export const ModularItemPanel = ({
  itemId,
  item: itemProp,
  viewPanel,
  tabs,
  expanded,
  panelTab,
  onJumpToItem,
  onTabChange,
  open: openProp,
  onToggle,
  onClose,
  customFieldDefinitions,
  enableAncestors,
  staticAncestors,
  disableAncestorNavigation,
}: ItemPanelProps) => {
  const { t } = useTranslation();
  const { openItemPanel, closeItemPanel } = useItemPanelNavigation();
  const permissions = usePermissionsWithPublished();
  const currentUserId = useCurrentUserId();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { data: fetchedItem, isLoading: isLoadingItem } = useItemFetchQueryWithPublished({
    itemId: itemId ?? 0,
    enabled: !!itemId && !itemProp,
  });

  const item = itemProp ?? fetchedItem;
  const {
    isOrphan,
    isLoading: isWorkIntegrationLoading,
    integrationId,
  } = useGetItemWorkIntegration(item);
  const { data: integrationsById } = useGetAllWorkIntegrationsById(
    { enabled: integrationId != null },
    integrationId != null ? { filter: { ids: [integrationId] } } : undefined,
  );
  const integration = integrationId != null ? integrationsById?.[integrationId] : undefined;
  const isIntegrationItem = !!item && item.externalKey != null && !isOrphan;
  const isItemCreator = !!item && !!currentUserId && item.createdBy === currentUserId;
  const hasDeletePermission = !!permissions?.edit_organization || isItemCreator;
  const canDeleteItem =
    !!item &&
    !isWorkIntegrationLoading &&
    !isIntegrationItem &&
    !viewPanel.isReadOnly &&
    hasDeletePermission;
  const showDisabledDelete =
    !!item && !isWorkIntegrationLoading && !canDeleteItem && !viewPanel.isReadOnly;
  const deleteTooltip = canDeleteItem
    ? t(($) => $.adaptivePlanner.itemPanel.delete.tooltip)
    : isIntegrationItem
      ? integration?.name
        ? t(($) => $.adaptivePlanner.itemPanel.delete.disabledSyncedTooltipNamed, {
            sourceSystemType: integration.sourceSystemType,
            integrationName: integration.name,
          })
        : t(($) => $.adaptivePlanner.itemPanel.delete.disabledSyncedTooltip)
      : t(($) => $.adaptivePlanner.itemPanel.delete.disabledNoPermissionTooltip);

  const togglePanel = () => {
    openItemPanel({
      itemId: itemId ?? item?.id,
      panelTab: panelTab,
      expanded: !expanded,
    });
  };

  const handleTabChange = (tab: TabValueType) => {
    openItemPanel({
      itemId: itemId ?? item?.id,
      panelTab: tab,
      expanded,
    });
    onTabChange?.(tab);
  };

  const handleToggle = onToggle ?? togglePanel;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsDeleteDialogOpen(false);
      if (onClose) {
        onClose();
      } else {
        closeItemPanel();
      }
    }
  };

  const isOpen = openProp ?? (!!itemId || !!itemProp);

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn('flex flex-col gap-0 [&>button]:hidden', {
          '!w-full !max-w-screen-lg xl:!max-w-6xl': expanded,
          '!max-w-2xl': !expanded,
        })}
      >
        <SheetHeader className="h-12 justify-center border-b-.5 border-border-neutral px-4 py-0">
          <SheetTitle className="sr-only" />
          <div className="flex items-center gap-1">
            <div className="flex-1 min-w-0 overflow-hidden">
              {item && (
                <BreadcrumbAncestors
                  item={item}
                  enabled={enableAncestors}
                  staticAncestors={staticAncestors}
                  disableNavigation={disableAncestorNavigation}
                />
              )}
            </div>
            {expanded ? (
              <TooltipIconButton
                className="shrink-0 p-1 cursor-pointer rounded-xs hover:bg-bg-neutral-hovered"
                icon={<CollapseContent />}
                tooltipText={t(($) => $.adaptivePlanner.itemPanel.collapsePanel)}
                onClick={handleToggle}
              />
            ) : (
              <TooltipIconButton
                className="shrink-0 p-1 cursor-pointer rounded-xs hover:bg-bg-neutral-hovered"
                icon={<ExpandContent />}
                tooltipText={t(($) => $.adaptivePlanner.itemPanel.expandPanel)}
                onClick={handleToggle}
              />
            )}
            <SheetClose asChild>
              <TooltipIconButton
                className="shrink-0 p-1 cursor-pointer rounded-xs opacity-70 hover:opacity-100 hover:bg-bg-neutral-hovered"
                icon={<Close className="size-4" />}
                tooltipText={t(($) => $.common.close)}
                testId="button-Close"
              />
            </SheetClose>
          </div>
        </SheetHeader>
        <SheetDescription className="sr-only" />
        {isLoadingItem && !item && (
          <div className="flex h-full w-full items-center justify-center">
            <Spinner size={32} />
          </div>
        )}
        {item && viewPanel.adapter ? (
          <TabAdapterProvider adapter={viewPanel.adapter}>
            <ItemNameField />
            <div className="flex h-full flex-row overflow-hidden bg-bg-surface-base">
              <div className="flex-1 overflow-hidden">
                <Suspense fallback={null}>
                  <ItemDetails
                    item={item}
                    tabs={tabs}
                    panelTab={panelTab}
                    expanded={expanded}
                    onTabChange={handleTabChange}
                    customFieldDefinitions={customFieldDefinitions}
                  />
                </Suspense>
              </div>
            </div>
          </TabAdapterProvider>
        ) : item ? (
          <div className="flex h-full flex-row overflow-hidden bg-bg-surface-base">
            <div className="flex-1 overflow-hidden">
              <Suspense fallback={null}>
                <ItemDetails
                  item={item}
                  tabs={tabs}
                  panelTab={panelTab}
                  expanded={expanded}
                  onTabChange={handleTabChange}
                  customFieldDefinitions={customFieldDefinitions}
                />
              </Suspense>
            </div>
          </div>
        ) : null}
        <SheetFooter className="border-t-1 border-border-neutral flex-row justify-between gap-2">
          <div className="flex gap-2">
            {(canDeleteItem || showDisabledDelete) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={deleteTooltip}
                    aria-disabled={!canDeleteItem}
                    data-testid={canDeleteItem ? 'button-DeleteItem' : 'button-DeleteItem-disabled'}
                    className={cn('text-text-danger', {
                      'hover:bg-bg-danger-subtle hover:text-text-danger': canDeleteItem,
                      'cursor-not-allowed opacity-60': !canDeleteItem,
                    })}
                    onClick={
                      canDeleteItem ? () => setIsDeleteDialogOpen(true) : (e) => e.preventDefault()
                    }
                  >
                    <Delete />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="whitespace-pre-line">{deleteTooltip}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex gap-2">
            <SheetClose asChild>
              <Button variant="outline">{t(($) => $.common.close)}</Button>
            </SheetClose>
            {onJumpToItem && (
              <SheetClose asChild>
                <Button
                  onClick={() => {
                    const id = itemId ?? item?.id;
                    if (id) {
                      onJumpToItem(id);
                    }
                  }}
                >
                  {t(($) => $.common.closeAndJumpToItem)}
                </Button>
              </SheetClose>
            )}
          </div>
        </SheetFooter>
      </SheetContent>
      {item && isDeleteDialogOpen && (
        <DeleteItemDialog item={item} onOpenChange={setIsDeleteDialogOpen} />
      )}
    </Sheet>
  );
};
